import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Package, MapPin, Phone, Mail, UserCheck,
  ShoppingCart, IndianRupee, Clock, CheckCircle2, XCircle,
  Truck, RefreshCw, X, Loader2, CreditCard, StickyNote,
  TrendingUp, Star, Store,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

const fmtDateShort = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });

const fmtRupee = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const fmtPhone = (phone) => {
  if (phone && phone.startsWith("91") && phone.length === 12)
    return `+91 ${phone.slice(2, 7)} ${phone.slice(7)}`;
  return phone;
};

const getImageUrl = (p) => {
  if (!p) return null;
  if (p.startsWith("http")) return p;
  if (p.startsWith("/uploads/")) return `${BACKEND_URL}/api${p}`;
  return `${BACKEND_URL}${p}`;
};

// ── status config ─────────────────────────────────────────────────────────────
const STATUS = {
  scheduled:        { label: "Scheduled",        color: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-400", icon: Clock },
  pending:          { label: "Pending",          color: "bg-amber-100 text-amber-700",   dot: "bg-amber-400",   icon: Clock },
  confirmed:        { label: "Confirmed",        color: "bg-blue-100 text-blue-700",     dot: "bg-blue-400",    icon: CheckCircle2 },
  processing:       { label: "Processing",       color: "bg-violet-100 text-violet-700", dot: "bg-violet-400",  icon: RefreshCw },
  out_for_delivery: { label: "Out for Delivery", color: "bg-sky-100 text-sky-700",       dot: "bg-sky-400",     icon: Truck },
  delivered:        { label: "Delivered",        color: "bg-green-100 text-green-700",   dot: "bg-green-400",   icon: CheckCircle2 },
  complete:         { label: "Complete",         color: "bg-teal-100 text-teal-700",     dot: "bg-teal-400",    icon: CheckCircle2 },
  cancelled:        { label: "Cancelled",        color: "bg-red-100 text-red-700",       dot: "bg-red-400",     icon: XCircle },
};

const StatusBadge = ({ status, large }) => {
  const cfg = STATUS[status] || { label: status, color: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${large ? "px-4 py-1.5 text-sm" : "px-2.5 py-1 text-xs"} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ── info card ─────────────────────────────────────────────────────────────────
const InfoCard = ({ title, icon: Icon, children, accent }) => (
  <div className={`bg-white rounded-2xl border ${accent ? "border-[#0A4CAE]/20" : "border-gray-200"} shadow-sm overflow-hidden`}>
    <div className={`px-6 py-4 border-b ${accent ? "border-[#0A4CAE]/10 bg-[#e8eef9]" : "border-gray-100"} flex items-center gap-2`}>
      {Icon && <Icon size={17} className={accent ? "text-[#0A4CAE]" : "text-gray-500"} />}
      <h2 className={`font-semibold text-sm ${accent ? "text-[#0A4CAE]" : "text-gray-700"}`}>{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ── stat tile ─────────────────────────────────────────────────────────────────
const StatTile = ({ icon: Icon, label, value, sub, bg, iconColor }) => (
  <div className={`rounded-xl p-4 flex items-center gap-3 ${bg}`}>
    <div className="p-2 bg-white/60 rounded-lg">
      <Icon size={18} className={iconColor} />
    </div>
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-bold text-gray-900 leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  </div>
);

// ── main page ─────────────────────────────────────────────────────────────────
const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [assigningManager, setAssigningManager] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState("");

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("gasman_token")}` },
  });

  const fetchDetail = async () => {
    try {
      const [detailRes, managersRes] = await Promise.all([
        axios.get(`${API}/admin/orders/${orderId}`, authHeaders()),
        axios.get(`${API}/admin/delivery-managers`, authHeaders()),
      ]);
      setData(detailRes.data);
      setManagers(managersRes.data);
      setSelectedManagerId(detailRes.data.order.delivery_manager_id || "");
    } catch (err) {
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, [orderId]);

  const handlePaymentStatusChange = async (newStatus) => {
    setUpdatingPayment(true);
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/payment-status?new_payment_status=${newStatus}`,
        {},
        authHeaders()
      );
      setData((prev) => ({ ...prev, order: { ...prev.order, payment_status: newStatus } }));
      toast.success(newStatus === "complete" ? "Marked as Paid" : "Marked as Pending");
    } catch {
      toast.error("Failed to update payment status");
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    // Update status directly for all statuses
    let cancelReason;
    if (newStatus === "cancelled") {
      cancelReason = window.prompt("Enter the cancellation reason:");
      if (!cancelReason?.trim()) {
        toast.error("Cancellation reason is required");
        return;
      }
    }
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, {}, {
        ...authHeaders(),
        params: { new_status: newStatus, ...(cancelReason ? { cancel_reason: cancelReason.trim() } : {}) },
      });
      toast.success(`Status updated to ${STATUS[newStatus]?.label}`);
      setData((prev) => ({
        ...prev,
        order: {
          ...prev.order,
          status: newStatus,
          ...(newStatus === "cancelled" ? {
            payment_status: "cancelled",
            cancel_reason: cancelReason.trim(),
          } : {}),
        },
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssignManager = async (managerId) => {
    setAssigningManager(true);
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/assign-manager`,
        { delivery_manager_id: managerId || null },
        authHeaders()
      );
      const mgr = managers.find((m) => m.id === managerId);
      setData((prev) => ({
        ...prev,
        order: {
          ...prev.order,
          delivery_manager_id: managerId || null,
          delivery_manager_name: mgr?.name || null,
          delivery_manager_phone: mgr?.phone || null,
        },
      }));
      setSelectedManagerId(managerId || "");
      toast.success(managerId ? `Assigned to ${mgr?.name}` : "Manager unassigned");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign manager");
    } finally {
      setAssigningManager(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#0A4CAE]" size={32} />
      </div>
    );
  }

  if (!data) return null;

  const { order, customer_stats, recent_customer_orders } = data;
  const addr = order.delivery_address || {};
  const statusCfg = STATUS[order.status] || {};

  return (
    <div>
      {/* ── Top bar ── */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/admin/orders")}
          className="flex items-center gap-2 text-gray-500 hover:text-[#0A4CAE] transition-colors text-sm font-medium"
        >
          <ArrowLeft size={18} />
          Back to Orders
        </button>
      </div>

      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart size={24} className="text-[#0A4CAE]" />
            Order Details
          </h1>
          <p className="font-mono text-sm text-gray-400 mt-1">{order.id}</p>
          <p className="text-sm text-gray-500 mt-0.5">{fmtDate(order.created_at)}</p>
          {order.scheduled_at && <p className="text-sm font-medium text-indigo-600 mt-1">Scheduled for {fmtDate(order.scheduled_at)}</p>}
          {order.cancel_reason && <p className="text-sm font-medium text-red-600 mt-1">Cancel reason: {order.cancel_reason}</p>}
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={order.status} large />
          <Select value={order.status} onValueChange={handleStatusChange} disabled={updatingStatus}>
            <SelectTrigger className="w-44 border-gray-300 text-sm h-9">
              <SelectValue placeholder="Change status" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* ── LEFT COLUMN (2/3) ── */}
        <div className="xl:col-span-2 space-y-6">

          {/* Order Items */}
          <InfoCard title="Order Items" icon={Package}>
            <div className="space-y-3">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                  {item.product_image ? (
                    <img src={getImageUrl(item.product_image)} alt={item.product_name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <Package size={20} className="text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{item.product_name}</p>
                    {item.show_price !== false && <p className="text-sm text-gray-500">{fmtRupee(item.product_price)} × {item.quantity}</p>}
                  </div>
                  {item.show_price !== false && <p className="font-bold text-gray-900 text-lg">{fmtRupee(item.subtotal)}</p>}
                </div>
              ))}
            </div>

            {/* Totals */}
            {order.order_type !== "remove_connection" && <div className="mt-5 pt-4 border-t border-gray-100 space-y-2">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Order Value ({order.total_items} items)</span>
                <span>{fmtRupee(order.order_value ?? order.subtotal_amount ?? order.total_amount)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Delivery</span>
                <span className="text-green-600 font-medium">Free</span>
              </div>
              {(order.loyalty_points_used ?? 0) > 0 && (
                <div className="flex justify-between text-sm font-medium text-emerald-600">
                  <span>Loyalty Points Used ({order.loyalty_points_used})</span>
                  <span>-{fmtRupee(order.loyalty_discount ?? order.loyalty_points_used)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg text-gray-900 pt-1">
                <span>Payable Amount</span>
                <span className="text-[#0A4CAE]">{fmtRupee(order.payable_amount ?? order.total_amount)}</span>
              </div>
              <div className="flex justify-between font-semibold text-sm border-t border-gray-100 pt-2">
                <span className="text-gray-600">Pending Balance</span>
                <span className={(order.pending_balance ?? order.total_amount) > 0 ? "text-red-600" : "text-green-600"}>
                  {fmtRupee(order.pending_balance ?? order.total_amount)}
                </span>
              </div>
            </div>}
          </InfoCard>

          {/* Delivery Address */}
          <InfoCard title="Delivery Address" icon={MapPin}>
            <div className="flex items-start justify-between gap-3">
              <div className="text-gray-700 space-y-1">
                {addr.address_name && <p className="font-semibold text-gray-900">{addr.address_name}</p>}
                <p>{addr.address_line_1}</p>
                {addr.address_line_2 && <p>{addr.address_line_2}</p>}
                <p>
                  {[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
                </p>
                {addr.country && <p className="text-gray-500">{addr.country}</p>}
              </div>
              {addr.latitude && addr.longitude && (
                <a
                  href={`https://www.google.com/maps?q=${addr.latitude},${addr.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs font-medium text-[#0A4CAE] bg-[#e8eef9] hover:bg-[#0A4CAE] hover:text-white px-3 py-2 rounded-lg transition-colors"
                  title="Open in Google Maps"
                >
                  <MapPin size={14} />
                  Maps
                </a>
              )}
            </div>
          </InfoCard>

          {/* Payment & Notes */}
          <InfoCard title="Payment & Notes" icon={CreditCard}>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 w-32">Payment Method</span>
                <span className="font-medium text-gray-900">{order.payment_method}</span>
              </div>
              {order.notes && (
                <div className="flex items-start gap-3">
                  <span className="text-sm text-gray-500 w-32 flex items-center gap-1">
                    <StickyNote size={13} /> Notes
                  </span>
                  <span className="text-gray-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 text-sm">{order.notes}</span>
                </div>
              )}
            </div>
          </InfoCard>

          {/* Customer's Recent Orders */}
          {recent_customer_orders.length > 0 && (
            <InfoCard title={`Other Orders by ${order.user_name}`} icon={TrendingUp}>
              <div className="divide-y divide-gray-50">
                {recent_customer_orders.map((ro) => (
                  <div
                    key={ro.id}
                    className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors"
                    onClick={() => navigate(`/admin/orders/${ro.id}`)}
                  >
                    <div>
                      <p className="font-mono text-xs text-gray-400">{ro.id.slice(0, 8)}…</p>
                      <p className="text-sm text-gray-600 mt-0.5">{ro.total_items} items · {fmtDateShort(ro.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="font-semibold text-gray-800 text-sm">{fmtRupee(ro.total_amount)}</span>
                      <StatusBadge status={ro.status} />
                    </div>
                  </div>
                ))}
              </div>
            </InfoCard>
          )}
        </div>

        {/* ── RIGHT COLUMN (1/3) ── */}
        <div className="space-y-6">

          {/* Assigned Store */}
          <InfoCard title="Assigned Store" icon={Store}>
            {order.store_name ? (
              <div className="flex items-center gap-2 bg-[#e8eef9] rounded-xl px-3 py-2.5">
                <Store size={15} className="text-[#0A4CAE] shrink-0" />
                <p className="text-sm font-semibold text-gray-900">{order.store_name}</p>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No store assigned</p>
            )}
          </InfoCard>

          {/* Assign Delivery Manager */}
          <InfoCard title="Delivery Manager" icon={UserCheck}>
            {order.delivery_manager_name && (
              <div className="flex items-center gap-2 mb-4 bg-[#e8eef9] rounded-xl px-3 py-2.5">
                <UserCheck size={15} className="text-[#0A4CAE]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{order.delivery_manager_name}</p>
                  {order.delivery_manager_phone && (
                    <p className="text-xs text-gray-500">{order.delivery_manager_phone}</p>
                  )}
                </div>
                <button
                  onClick={() => handleAssignManager("")}
                  disabled={assigningManager}
                  className="text-red-400 hover:text-red-600 transition-colors"
                  title="Remove"
                >
                  <X size={15} />
                </button>
              </div>
            )}
            <div className="space-y-2">
              <Select value={selectedManagerId} onValueChange={setSelectedManagerId}>
                <SelectTrigger className="w-full border-gray-300 text-sm">
                  <SelectValue placeholder="Select a manager…" />
                </SelectTrigger>
                <SelectContent>
                  {managers.length === 0 ? (
                    <SelectItem value="__empty__" disabled>No managers added yet</SelectItem>
                  ) : managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} · {m.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => handleAssignManager(selectedManagerId)}
                disabled={assigningManager || !selectedManagerId}
                className="w-full bg-[#0A4CAE] hover:bg-[#083d8a] text-white text-sm"
              >
                {assigningManager ? <Loader2 size={15} className="animate-spin mr-1" /> : null}
                {order.delivery_manager_name ? "Reassign Manager" : "Assign Manager"}
              </Button>
            </div>
          </InfoCard>

          {/* Payment Status */}
          <InfoCard title="Payment Status" icon={CreditCard}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">Current Status</p>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold mt-1 ${
                  order.payment_status === "not_applicable"
                    ? "bg-gray-100 text-gray-600"
                    : order.status === "cancelled" || order.payment_status === "cancelled"
                    ? "bg-red-100 text-red-700"
                    : order.payment_status === "complete"
                    ? "bg-green-100 text-green-700"
                    : "bg-amber-50 text-amber-700"
                }`}>
                  {order.payment_status === "not_applicable"
                    ? "Not applicable"
                    : order.status === "cancelled" || order.payment_status === "cancelled"
                    ? "Cancelled"
                    : order.payment_status === "complete"
                      ? "✓ Paid"
                      : "⏳ Pending"}
                </span>
              </div>
            </div>
            {order.payment_status !== "not_applicable" && <div className="flex gap-2">
              <Button
                onClick={() => handlePaymentStatusChange("pending")}
                disabled={updatingPayment || order.status === "cancelled" || order.payment_status === "pending"}
                variant="outline"
                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50 text-sm disabled:opacity-40"
              >
                Pending
              </Button>
              <Button
                onClick={() => handlePaymentStatusChange("complete")}
                disabled={updatingPayment || order.status === "cancelled" || order.payment_status === "complete"}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm disabled:opacity-40"
              >
                {updatingPayment ? <Loader2 size={14} className="animate-spin" /> : "Mark Paid"}
              </Button>
            </div>}
          </InfoCard>

          {/* Customer Info */}
          <InfoCard title="Customer" icon={Phone} accent>            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0A4CAE] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {order.user_name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{order.user_name}</p>
                  <p className="text-xs text-gray-400">Customer</p>
                </div>
              </div>
              <div className="pt-2 space-y-2 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone size={14} className="text-[#0A4CAE]" />
                  {fmtPhone(order.user_phone)}
                </div>
                {order.user_email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail size={14} className="text-[#0A4CAE]" />
                    {order.user_email}
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-1 border-[#0A4CAE]/30 text-[#0A4CAE] hover:bg-[#e8eef9] text-xs"
                onClick={() => navigate(`/admin/users/${order.user_id}`)}
              >
                View Full Profile
              </Button>
            </div>
          </InfoCard>

          {/* Customer Stats */}
          <InfoCard title="Customer Stats" icon={Star}>
            <div className="grid grid-cols-2 gap-3">
              <StatTile
                icon={ShoppingCart}
                label="Total Orders"
                value={customer_stats.total_orders}
                bg="bg-[#e8eef9]"
                iconColor="text-[#0A4CAE]"
              />
              <StatTile
                icon={IndianRupee}
                label="Total Spent"
                value={fmtRupee(customer_stats.total_spent)}
                bg="bg-orange-50"
                iconColor="text-[#F7851E]"
              />
              <StatTile
                icon={MapPin}
                label="Addresses"
                value={customer_stats.total_addresses}
                bg="bg-green-50"
                iconColor="text-green-600"
              />
              <StatTile
                icon={CheckCircle2}
                label="Delivered"
                value={customer_stats.status_breakdown?.delivered || 0}
                bg="bg-emerald-50"
                iconColor="text-emerald-600"
              />
            </div>

            {/* Status breakdown */}
            {Object.keys(customer_stats.status_breakdown || {}).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Order Breakdown</p>
                {Object.entries(customer_stats.status_breakdown).map(([s, count]) => {
                  const cfg = STATUS[s] || { label: s, dot: "bg-gray-300" };
                  const total = customer_stats.total_orders || 1;
                  return (
                    <div key={s} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                      <span className="text-xs text-gray-600 flex-1">{cfg.label}</span>
                      <span className="text-xs font-semibold text-gray-700">{count}</span>
                      <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0A4CAE] rounded-full"
                          style={{ width: `${(count / total) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </InfoCard>

        </div>
      </div>

    </div>
  );
};

export default OrderDetailPage;
